import { useState, useEffect, useMemo, useRef } from 'react'
import { SelectItem } from '@kistill/client/data-access'
import { useCustomerAutocompleteOptionsLazyQuery } from '@kistill/client/data-access'
import { AutocompleteElement } from 'react-hook-form-mui'
import {
  AutocompleteInputChangeReason,
  AutocompleteChangeReason,
  AutocompleteChangeDetails,
} from '@mui/material'

const tokenize = (value: string) => (value ?? '').toLowerCase().trim().split(/\s+/)

export const matchesCustomerQuery = (title: string, query: string) => {
  const normalizedTitle = (title ?? '').toLowerCase().trim()
  const normalizedQuery = (query ?? '').toLowerCase().trim()
  if (!normalizedQuery) return true
  if (normalizedTitle.startsWith(normalizedQuery)) return true
  return tokenize(normalizedTitle).some((w) => w.startsWith(normalizedQuery))
}

export const scoreCustomerOption = (title: string, query: string) => {
  const normalizedTitle = (title ?? '').toLowerCase().trim()
  const normalizedQuery = (query ?? '').toLowerCase().trim()
  if (!normalizedQuery) return 0
  if (normalizedTitle === normalizedQuery) return 100
  if (normalizedTitle.startsWith(normalizedQuery)) return 80
  if (tokenize(normalizedTitle).some((w) => w.startsWith(normalizedQuery)))
    return 60
  return 0
}

export const filterAndSortCustomerOptions = (
  options: SelectItem[],
  query: string,
) => {
  // Don't prune server hits on the client (wildcards/fuzziness handled server-side).
  // We only sort by score so results don't "disappear" from the dropdown.
  return [...options].sort((a, b) => {
    const scoreDiff =
      scoreCustomerOption(b.title ?? '', query) -
      scoreCustomerOption(a.title ?? '', query)
    if (scoreDiff !== 0) return scoreDiff
    return (a.title ?? '').localeCompare(b.title ?? '', 'is')
  })
}

interface KistillFormAutocompleteCustomerProps {
  name: string
  label: string
  required?: boolean
  multiple?: boolean
  selectedContacts?: SelectItem[]
  setSelectedContacts?: (selectedContacts: SelectItem[]) => void
  onChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: SelectItem[],
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<unknown> | undefined,
  ) => void
  selectedContact?: SelectItem
  setSelectedContact?: (selectedContacts: SelectItem) => void
}

export function KistillFormAutocompleteCustomer({
  name,
  label,
  required,
  multiple,
  selectedContacts,
  setSelectedContacts,
  selectedContact,
  onChange: onChangeProp,
}: KistillFormAutocompleteCustomerProps) {
  const [customerOptions, setCustomerOptions] = useState<SelectItem[]>([])
  const latestRequestIdRef = useRef(0)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // MUI's `state.inputValue` can lag right after erase->type (your reported bug).
  // Keep the latest typed value in a ref so filtering always uses the same value
  // that triggered this handler.
  const latestTypedValueRef = useRef('')

  const [getCustomerOptions] = useCustomerAutocompleteOptionsLazyQuery({
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    // Initial load: populate dropdown when the user opens it without typing.
    const requestId = latestRequestIdRef.current + 1
    latestRequestIdRef.current = requestId

    getCustomerOptions({ variables: { query: '' } }).then((response) => {
      if (requestId !== latestRequestIdRef.current) return

      const mappedOptions =
        response.data?.customerOptions?.map((x) => ({
          id: x?.id,
          title: x.title,
        })) ?? []

      setCustomerOptions(mappedOptions)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCustomerOptions])

  const dedupeById = (options: SelectItem[]) =>
    Array.from(new Map(options.map((x) => [x.id, x])).values())

  const allOptions = useMemo(() => {
    // customerOptions is last so it wins in dedupeById (Map keeps last entry per id),
    // ensuring server-fetched names overwrite empty-title placeholders in selectedContacts.
    const merged = [
      ...(selectedContacts ?? []),
      ...(selectedContact ? [selectedContact] : []),
      ...(customerOptions ?? []),
    ]
    return dedupeById(merged)
  }, [customerOptions, selectedContacts, selectedContact])

  const handleInputChange = (
    _event: React.SyntheticEvent<Element, Event>,
    value: string,
    _reason: AutocompleteInputChangeReason,
  ) => {
    const q = value?.trim() ?? ''
    latestTypedValueRef.current = q

    // Clear cached options while the next query is in flight (non-empty and
    // empty paths) so stale search hits are not shown after clearing the box.
    setCustomerOptions([])

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    const requestId = latestRequestIdRef.current + 1
    latestRequestIdRef.current = requestId

    // Debounce network calls; bump id immediately so responses still in flight
    // from earlier debounced work cannot apply after newer input.
    debounceRef.current = setTimeout(() => {
      getCustomerOptions({
        variables: { query: q },
      }).then((response) => {
        if (requestId !== latestRequestIdRef.current) return

        const mappedOptions =
          response.data?.customerOptions?.map((x) => ({
            id: x?.id,
            title: x.title,
          })) ?? []

        setCustomerOptions(dedupeById(mappedOptions))
      })
    }, 120)
  }

  const handleAutocompleteChange = (
    // eslint-disable-next-line @typescript-eslint/ban-types
    event: React.ChangeEvent<{}>,
    value: SelectItem | SelectItem[] | null,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<unknown>,
  ) => {
    const valueAsArray = Array.isArray(value) ? value : value ? [value] : []
    if (setSelectedContacts) {
      if (reason === 'selectOption') {
        setSelectedContacts(valueAsArray)
      } else if (reason === 'removeOption') {
        // Filter out the removed option from the value array
        const filteredValue = valueAsArray.filter(
          (item) => item !== null && item !== undefined,
        )
        setSelectedContacts(filteredValue)
      } else if (reason === 'clear') {
        setSelectedContacts([])
      }
    }
    onChangeProp?.(event as React.SyntheticEvent<Element, Event>, valueAsArray, reason, details)
  }

  return (
    <AutocompleteElement
      multiple={multiple}
      required={required}
      name={name}
      label={label}
      options={allOptions}
      autocompleteProps={{
        clearOnBlur: false,
        onInputChange: handleInputChange,
        // Client-side guard: use latestTypedValueRef so filtering matches the
        // synchronous onInputChange value (React query state can lag one render).
        filterOptions: (options, _state) => {
          const q = latestTypedValueRef.current.trim()
          const deduped = dedupeById(options as SelectItem[])
          if (!q) return deduped
          return filterAndSortCustomerOptions(deduped, q)
        },
        getOptionLabel: (option) => option?.title,
        renderOption: (props, option, _state) => (
          <li {...props} key={option?.id}>
            {option?.title}
          </li>
        ),
        onChange: handleAutocompleteChange,
      }}
    />
  )
}
